import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ISkyApiUploadHandlers, SkyApiService } from '@skyeng/libs/shared/api';
import { VimCoreAuthService } from '@skyeng/libs/vimbox-core/user';
import { VimCoreBaseConfig } from '@skyeng/libs/vimbox-core/base';
import { switchMap, map, mapTo } from 'rxjs/operators';

interface UploadedFile {
  title: string;
  url: string;
}

interface UploadIntention {
  url: string;
  inputs: {
    [key: string]: string;
    Policy: string;
    'X-Amz-Algorithm': string;
    'X-Amz-Credential': string;
    'X-Amz-Date': string;
    'X-Amz-Signature': string;
    acl: string;
    key: string;
    success_action_status: string;
  };
}

@Injectable({ providedIn: 'root' })
export class FileUploadApiService {
  private skyApiService = inject(SkyApiService);
  private vimCoreAuthService = inject(VimCoreAuthService);
  private vimCoreBaseConfig = inject(VimCoreBaseConfig);
  private skyproApi = [this.vimCoreBaseConfig.boilerplateEndpoint];

  /**
   * Загружает файл в s3
   * Используй uploadEventHandlers: loaded, progress, error чтобы следить за состоянием загрузки
   */
  upload(file: File, uploadEventHandlers: ISkyApiUploadHandlers = {}): Observable<UploadedFile> {
    return this.createUploadIntention(file).pipe(
      switchMap(uploadIntention => this.uploadFile(uploadIntention, file, uploadEventHandlers)),
      map(uploadIntention => {
        return {
          title: file.name || 'unnamed',
          url: uploadIntention.url + '/' + uploadIntention.inputs.key,
        };
      }),
    );
  }

  /**
   * загружает файл по UploadIntention
   */
  private uploadFile(
    uploadIntention: UploadIntention,
    file: File,
    uploadEventHandlers: ISkyApiUploadHandlers = {},
  ): Observable<UploadIntention> {
    const formParams = new FormData();

    Object.keys(uploadIntention.inputs).forEach(key => formParams.append(key, uploadIntention.inputs[key]));

    formParams.append('file', file);

    return this.skyApiService
      .post<void>([''], uploadIntention.url, {
        formParams,
        responseType: 'text',
        longRequest: true,
        uploadHandlers: uploadEventHandlers,
      })
      .pipe(mapTo(uploadIntention));
  }

  /**
   * Создает UploadIntention для файла, с его помощью фронтенд сможет загрузить этот файл на s3.
   * Для загрузки файла по UploadIntention используй метод upload
   */
  private createUploadIntention(file: File): Observable<UploadIntention> {
    return this.skyApiService.post(this.skyproApi, '/api/v1/file/upload-intention', {
      formParams: { name: file.name, size: file.size, type: file.type },
      withCredentials: true,
      token: this.vimCoreAuthService.getToken(),
    });
  }
}
